import { type Control, FieldPath, FieldValues, type UseFormWatch } from 'react-hook-form';
import { DatePickerField, SelectField, TextInputField } from '@brighte/form-components';
import { AU_STATES_CODE } from '@brighte/validation';
import { Columns } from '@spark-web/columns';
import { IDENTIFICATION, LABELS } from '@utils';

type SmeDriverLicenceFieldsProps<TFieldValues extends FieldValues> = {
  name: string;
  control: Control<TFieldValues>;
  watch: UseFormWatch<TFieldValues>;
};

function SmeDriverLicenceFields<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  watch,
}: Readonly<SmeDriverLicenceFieldsProps<TFieldValues>>) {
  const { DRIVER_LICENCE_NUMBER, LICENCE_CARD_NUMBER, EXPIRY_DATE } = LABELS;
  const { IDENTIFICATION_LICENCE_DESCRIPTION } = IDENTIFICATION;
  const stateCode = watch(`${name}.drivingLicense.state` as FieldPath<TFieldValues>);

  return (
    <>
      <TextInputField<TFieldValues>
        name={`${name}.drivingLicense.number` as FieldPath<TFieldValues>}
        control={control}
        label={DRIVER_LICENCE_NUMBER}
        maxLength={10}
        FieldProps={{
          description: stateCode
            ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode]?.LICENCE_NUMBER_DESCRIPTION
            : "This is printed on the front of your card and labeled as 'Licence Number'",
        }}
        autoComplete="off"
      />
      <TextInputField
        name={`${name}.drivingLicense.cardNumber` as FieldPath<TFieldValues>}
        control={control}
        label={LICENCE_CARD_NUMBER}
        maxLength={10}
        FieldProps={{
          description: stateCode
            ? IDENTIFICATION_LICENCE_DESCRIPTION[stateCode]?.LICENCE_CARD_NUMBER_DESCRIPTION
            : "This is printed on the front of your card and labeled as 'Card Number'",
        }}
        autoComplete="off"
      />
      <SelectField
        name={`${name}.drivingLicense.state` as FieldPath<TFieldValues>}
        control={control}
        label={LABELS.ISSUE_STATE}
        options={AU_STATES_CODE}
        placeholder="Select state"
      />
      <Columns gap="xxlarge" collapseBelow="tablet" template={[1, 1]}>
        <DatePickerField
          name={`${name}.drivingLicense.expiryDate` as FieldPath<TFieldValues>}
          control={control}
          label={EXPIRY_DATE}
          autoComplete="off"
        />
        <DatePickerField
          name={`${name}.dateOfBirth` as FieldPath<TFieldValues>}
          control={control}
          label={LABELS.DATE_OF_BIRTH}
          autoComplete="off"
        />
      </Columns>
    </>
  );
}

export default SmeDriverLicenceFields;
